import { useState, useCallback, useLayoutEffect, useRef, useEffect } from 'react';

/* eslint-disable sort-keys */
var useResizeObserver = element => {
  // eslint-disable-next-line sort-keys
  var [size, setSize] = useState({
    width: 0,
    height: 0
  });
  var handleResize = useCallback(entries => {
    if (!Array.isArray(entries)) {
      return;
    }
    var [{
      contentRect: {
        width,
        height
      }
    }] = entries;
    setSize({
      width,
      height
    });
  }, []);
  useLayoutEffect(() => {
    if (!element) {
      setSize({
        width: 0,
        height: 0
      });
      return;
    }
    var resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(element);
    // eslint-disable-next-line consistent-return
    return () => resizeObserver.disconnect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element]);
  return size;
};

/**
 * When provided with a wrapper (parent <ul>) and items (child <li>), it will return an array with item visibility
 * @param {HTMLUListElement} wrapper the <ul> wrapper to interact with
 * @param {Array.<HTMLLIElement|null>} items array of html <li> elements to check if in view
 * @returns {boolean[]} array mapping the visibility of list of elements provided in items
 */
var useIntersectionObserver = (wrapper, items) => {
  var observerRef = useRef(null);
  var domChangedRef = useRef(false);
  var [itemsVisibility, setItemsVisibility] = useState([]);
  var [changedEntries, setChangedEntries] = useState([]);
  useEffect(() => {
    if (domChangedRef.current) {
      setItemsVisibility(changedEntries.reduce((accumulator, changedEntry) => {
        var index = accumulator.findIndex(findEntry => findEntry.target === changedEntry.target);
        if (index === -1) {
          return [...accumulator, changedEntry];
        }
        accumulator[index] = changedEntry;
        return accumulator;
      }, changedEntries[0] ? [changedEntries[0]] : []).map(entry => entry.intersectionRatio === 1));
      domChangedRef.current = false;
      return;
    }
    setItemsVisibility(itemsVisibility.map((itemVisibility, index) => {
      var changedEntry = changedEntries.find(entry => entry.target === items[index]);
      if (changedEntry) {
        return changedEntry.intersectionRatio === 1;
      }
      return itemVisibility;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedEntries]);
  useEffect(() => {
    if (!wrapper || !items) {
      return;
    }
    if (!items.length) {
      setItemsVisibility([]);
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    domChangedRef.current = true;
    observerRef.current = new IntersectionObserver(setChangedEntries, {
      root: wrapper,
      threshold: 1.0
    });
    items.forEach(item => {
      var _observerRef$current;
      if (!item) {
        return;
      }
      (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 ? void 0 : _observerRef$current.observe(item);
    });
    // eslint-disable-next-line consistent-return
    return () => {
      items.map(item => {
        var _observerRef$current2;
        return item && ((_observerRef$current2 = observerRef.current) === null || _observerRef$current2 === void 0 ? void 0 : _observerRef$current2.unobserve(item));
      });
    };
  }, [items, wrapper]);
  return itemsVisibility;
};
var useWindowResizeListener = windowObj => {
  var [size, setSize] = useState({
    height: windowObj.innerHeight,
    width: windowObj.innerWidth
  });
  var handleResize = useCallback(() => {
    setSize({
      width: windowObj.innerWidth,
      height: windowObj.innerHeight
    });
  }, [windowObj.innerHeight, windowObj.innerWidth]);
  useEffect(() => {
    windowObj.addEventListener('resize', handleResize);
    return () => windowObj.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowObj]);
  return size;
};
var getCssBreakpoints = () => {
  var rootStyles = window.getComputedStyle(document.documentElement);
  return ['xs', 'sm', 'md', 'lg', 'xl'].reduce((accumulator, breakpoint) => {
    // @ts-ignore
    // eslint-disable-next-line radix
    accumulator[breakpoint] = parseInt(rootStyles.getPropertyValue("--breakpoint-".concat(breakpoint)));
    return accumulator;
  }, {});
};
var setRootCssVariable = (property, value) => {
  document.documentElement.style.setProperty(property, value);
};
var breakpointMapping = {
  xs: {
    itemMarginLeft: 8,
    peek: true,
    width: 0
  },
  sm: {
    itemMarginLeft: 12,
    peek: false,
    width: 0
  },
  md: {
    itemMarginLeft: 12,
    peek: false,
    width: 0
  },
  lg: {
    itemMarginLeft: 16,
    peek: false,
    width: 0
  },
  xl: {
    itemMarginLeft: 16,
    peek: false,
    width: 0
  }
};
var getCurrentPageSize = (pageSizeForBreakPoint, itemCount) => pageSizeForBreakPoint > itemCount ? itemCount : pageSizeForBreakPoint;
var getScrollSnapAlign = currentPageSize => currentPageSize % 2 === 1 ? 'center' : 'start';
var getPeekWidth = (wrapperWidth, itemMarginLeft, currentPageSize, itemWidth) => (wrapperWidth - itemMarginLeft * 2 * (currentPageSize + 1) - itemWidth * currentPageSize) / 2;
var getItemWidth = (wrapperWidth, itemMarginLeft, currentPageSize, peek) => (wrapperWidth * (peek ? 0.85 : 1) - itemMarginLeft * 2 * (peek ? currentPageSize : currentPageSize - 1)) / currentPageSize;
var getScrollMargin = (scrollSnapAlign, margin, peekWidth, peek) => scrollSnapAlign === 'center' || !peek ? 0 : margin * 2 + peekWidth;
var getBreakpointName = (_breakpointMapping, windowWidth) => Object.keys(_breakpointMapping).sort((cssBreakpointName1, cssBreakpointName2) => _breakpointMapping[cssBreakpointName2].width - _breakpointMapping[cssBreakpointName1].width).find(cssBreakpointName => windowWidth >= _breakpointMapping[cssBreakpointName].width) || 'xs';
var makeArrowButtonOnClick = (left, wrapper) => () => {
  wrapper === null || wrapper === void 0 ? void 0 : wrapper.scrollBy({
    behavior: 'smooth',
    left,
    top: 0
  });
};
var wrapperScrollTo = (left, wrapper) => {
  wrapper === null || wrapper === void 0 ? void 0 : wrapper.scrollTo({
    behavior: 'smooth',
    left,
    top: 0
  });
};
var getLabelText = itemsVisibility => "".concat(itemsVisibility.map((itemVisibility, index) => itemVisibility ? index + 1 : itemVisibility).filter(itemVisibility => itemVisibility).toString() || '..', " of ").concat(itemsVisibility.length);
var getIsScrollable = (currentPageSize, itemCount) => itemCount > currentPageSize;
var getIsPeek = (isScrollable, isPeekEnabled) => isScrollable && isPeekEnabled;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var isTruthy = value => !!value;

export { breakpointMapping, getBreakpointName, getCssBreakpoints, getCurrentPageSize, getIsPeek, getIsScrollable, getItemWidth, getLabelText, getPeekWidth, getScrollMargin, getScrollSnapAlign, isTruthy, makeArrowButtonOnClick, setRootCssVariable, useIntersectionObserver, useResizeObserver, useWindowResizeListener, wrapperScrollTo };
